import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import { CreditCardOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AppState } from '../store';
import { preparePublicPayPhoneTrialFromBridge } from '../store/user/thunks';
import './PayPhonePublic.css';
import bluemedixLogo from './assets/bluemedix-white.png';
import {
    getInitialPayPhoneLanguage,
    payPhoneBridgeText,
    PayPhoneLanguage,
    persistPayPhoneLanguage
} from './payPhonePublicLanguage';

const { Text, Title } = Typography;
const pendingPayPhoneStorageKey = 'tbc.payphone.pendingContractPayment';
const bridgeTokenStoragePrefix = 'tbc.payphone.bridgeToken.';

const PayPhoneBridge: React.FC<any> = (props: any) => {
    const location = useLocation();
    const [error, setError] = useState('');
    const [payPhoneUrl, setPayPhoneUrl] = useState('');
    const [loading, setLoading] = useState(true);
    const [language, setLanguage] = useState<PayPhoneLanguage>(getInitialPayPhoneLanguage);

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = query.get('token') || '';
    const t = payPhoneBridgeText[language];

    const handleLanguageChange = (nextLanguage: PayPhoneLanguage) => {
        setLanguage(nextLanguage);
        persistPayPhoneLanguage(nextLanguage);
    };

    useEffect(() => {
        document.documentElement.lang = language;
    }, [language]);

    const preparePayment = async () => {
        if (!token) {
            setError(t.missingToken);
            setLoading(false);
            return;
        }

        setError('');
        setLoading(true);

        try {
            const response = await props.preparePublicPayPhoneTrialFromBridge({ token });
            const url = response?.payWithCard || response?.payWithPayPhone || '';

            if (!url || !response?.clientTransactionId) {
                setError(t.linkError);
                setLoading(false);
                return;
            }

            localStorage.setItem(`${bridgeTokenStoragePrefix}${response.clientTransactionId}`, token);

            const pendingRaw = localStorage.getItem(pendingPayPhoneStorageKey);
            if (pendingRaw) {
                try {
                    const pending = JSON.parse(pendingRaw);
                    localStorage.setItem(pendingPayPhoneStorageKey, JSON.stringify({
                        ...pending,
                        clientTransactionId: response.clientTransactionId,
                        bridgeToken: token
                    }));
                } catch {
                    localStorage.setItem(pendingPayPhoneStorageKey, JSON.stringify({
                        clientTransactionId: response.clientTransactionId,
                        bridgeToken: token
                    }));
                }
            }

            setPayPhoneUrl(url);
            window.setTimeout(() => {
                window.location.replace(url);
            }, 1800);
        } catch {
            setError(t.prepareError);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        preparePayment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <main className="bm-payphone-page">
            <div className="bm-payphone-shape bm-payphone-shape-one" />
            <div className="bm-payphone-shape bm-payphone-shape-two" />
            <div className="bm-payphone-shape bm-payphone-shape-three" />
            <section className="bm-payphone-container bm-payphone-bridge-container">
                <header className="bm-payphone-topbar">
                    <img src={bluemedixLogo} alt="BlueMedix" className="bm-payphone-logo" />
                    <div className="bm-payphone-language">
                        <button type="button" className={language === 'es' ? 'active' : ''} onClick={() => handleLanguageChange('es')}>
                            ES
                        </button>
                        <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => handleLanguageChange('en')}>
                            EN
                        </button>
                    </div>
                </header>

                <section className="bm-payphone-glass bm-payphone-bridge-card">
                    {error ? (
                        <>
                            <div className="bm-payphone-error-icon">
                                <CreditCardOutlined />
                            </div>
                            <Title level={3}>{t.errorTitle}</Title>
                            <Text>{error}</Text>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={preparePayment}>
                                {t.retry}
                            </Button>
                        </>
                    ) : (
                        <>
                            <div className="bm-payphone-bridge-icon">
                                <SafetyCertificateOutlined />
                            </div>
                            <Title level={3}>{t.title}</Title>
                            <Text>
                                {t.subtitle}
                            </Text>

                            <div className="bm-payphone-progress">
                                <div className="done">
                                    <span />
                                    <div>
                                        <strong>{t.stepOneTitle}</strong>
                                        <p>{t.stepOneText}</p>
                                    </div>
                                </div>
                                <div className={payPhoneUrl ? 'done' : ''}>
                                    <span />
                                    <div>
                                        <strong>{t.stepTwoTitle}</strong>
                                        <p>{t.stepTwoText}</p>
                                    </div>
                                </div>
                            </div>

                            <Alert
                                type="info"
                                showIcon
                                message={t.alertTitle}
                                description={t.alertText}
                            />

                            <div className="bm-payphone-actions">
                                <Button
                                    type="primary"
                                    icon={<CreditCardOutlined />}
                                    disabled={!payPhoneUrl}
                                    onClick={() => payPhoneUrl && window.location.replace(payPhoneUrl)}
                                >
                                    {t.continue}
                                </Button>
                                {loading && <Spin />}
                            </div>
                        </>
                    )}
                </section>
            </section>
        </main>
    );
};

const mapStateToProps = (state: AppState) => ({
    messageResponse: state.system.messageResponse
});

export default connect(mapStateToProps, {
    preparePublicPayPhoneTrialFromBridge
})(PayPhoneBridge);
